import { useCurrentAccount } from '@application/api';
import { useMemo } from 'react';
import { BREEDER_ONBOARDING_MAX_STEP } from '@application/constants';
import { useValidationSchema } from '../useValidationSchema';
import { useGetProfile } from './useGetProfile';
import { useProfileInitialValuesBuilders } from './useProfileInitialValuesBuilder';

export const useCurrentProfileStep = () => {
  const { currentAccount } = useCurrentAccount();
  const { profile, profileLoading } = useGetProfile();
  const { profileInitialValues } = useProfileInitialValuesBuilders(['fr', 'nl', 'de'], profile);
  const { onboardingFormValidationSchemas } = useValidationSchema();

  const hasFinishedOnboarding = profile?.dogBreeder?.hasFinishedOnboarding ?? false;
  const hasAddedAnimals = profile?.animals && profile?.animals.length > 0;
  const hasAddedLitters = profile?.dogBreeder?.litters && profile?.dogBreeder?.litters.length > 0;

  const profileValidated = currentAccount?.dogBreeder?.certified ?? false;

  const currentProfileStep = useMemo(() => {
    if (profileValidated) return 13;

    let step = 1;
    for (let i = 1; i < BREEDER_ONBOARDING_MAX_STEP; i += 1) {
      if (onboardingFormValidationSchemas[i].isValidSync(profileInitialValues)) {
        step += 1;
      } else {
        return step;
      }
    }
    return step;
  }, [profileInitialValues, profileValidated, onboardingFormValidationSchemas]);

  // Max progress is 5
  // +1: registered
  // +1: completed questionnaire
  // +1: verified
  // +1: added animal
  // +1: added litter
  const currentProfileProgress = useMemo(() => {
    let progress = 1;

    if (hasFinishedOnboarding) progress += 1;
    if (hasAddedAnimals) progress += 1;
    if (hasAddedLitters) progress += 1;
    if (profileValidated) progress += 1;

    return progress > 5 ? 5 : progress;
  }, [hasAddedAnimals, hasAddedLitters, hasFinishedOnboarding, profileValidated]);

  const onboardingStep = useMemo(() => {
    let step = 1;
    if (hasFinishedOnboarding) step += 1;
    if (profileValidated) step += 1;
    return step > 3 ? 3 : step;
  }, [hasFinishedOnboarding, profileValidated]);

  const profileLocked = useMemo(() => {
    if (profileValidated || hasFinishedOnboarding) return false;
    if (currentProfileStep < BREEDER_ONBOARDING_MAX_STEP) return true;
    if (!currentAccount?.dogBreeder?.hasStartedOnboarding) return true;
    return false;
  }, [
    currentAccount?.dogBreeder?.hasStartedOnboarding,
    currentProfileStep,
    hasFinishedOnboarding,
    profileValidated,
  ]);

  const profileLinksLocked = useMemo(() => {
    if (currentProfileStep < BREEDER_ONBOARDING_MAX_STEP || !hasFinishedOnboarding) return true;
    return false;
  }, [currentProfileStep, hasFinishedOnboarding]);

  return {
    profileValidated,
    profileLoading,
    profileLocked,
    profileLinksLocked,
    currentProfileStep,
    currentProfileProgress,
    hasAddedAnimals,
    hasAddedLitters,
    onboardingStep,
  };
};
